import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../utils/useApi";
import apis from "../utils/apis";
import { setUserInfo } from "../store/slices/userSlice";
import { isTimeInRange, clearLocalStorageExcept } from "../utils/utils";
import { useNavigate } from "react-router-dom";
import { resetReduxStore, clearUserStorage } from "../utils/reduxUtils";

const useUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.user.userInfo);
  const { callApi: callUserDetails } = useApi();
  const userDefaultAddressId = userInfo?.defaultAddress?._id;

  const fetchUserDetails = useCallback(async () => {
    try {
      const userDetails = await callUserDetails({ api: apis.getUserDetails });
      const user = userDetails?.user;

      if (user) {
        // Check if user is deleted - logout user if true
        if (user?.isDeleted === true) {
          // Get current user ID before clearing
          const userId = user?.id || user?._id || "guest";

          // Clear user-specific Redux state storage
          clearUserStorage(userId);

          // Reset all Redux slices to initial state
          resetReduxStore(dispatch);

          // Get all reduxState keys to preserve
          const reduxStateKeys = Object.keys(localStorage).filter(key => key.startsWith('reduxState_'));
          
          // Clear other localStorage except language, firstLogin, and all Redux state keys
          clearLocalStorageExcept({ values: ["language", "firstLogin", ...reduxStateKeys] });

          navigate("/login");
          return null;
        }

        const startTime = user?.defaultAddress?.nearby?.mart?.startTime;
        const endTime = user?.defaultAddress?.nearby?.mart?.endTime;
        const isNotAvailable =
          !user?.defaultAddress?.nearby?.mart?.availabilityStatus;
        const isCurrentTimeNotInMartTimeRange = !isTimeInRange(
          startTime,
          endTime
        );

        // Add mart close status
        user.isMartClose = isCurrentTimeNotInMartTimeRange || isNotAvailable;

        // Update Redux store
        dispatch(setUserInfo(user));

        // Update localStorage with default location
        if (user?.defaultAddress?._id) {
          localStorage.setItem("defaultLocationId", user?.defaultAddress?._id);
        }
      }

      return user;
    } catch (err) {
      console.error("Error fetching user details:", err);
      
      // Handle user not found / deleted user
      if (
        (err?.success === false && err?.message === "User not found") ||
        err?.message?.trim()?.toLowerCase().includes("notfound")
      ) {
        // Get current user ID before clearing
        const userId = userInfo?.id || userInfo?._id || "guest";

        // Clear user-specific Redux state storage
        clearUserStorage(userId);

        // Reset all Redux slices to initial state
        resetReduxStore(dispatch);

        // Get all reduxState keys to preserve
        const reduxStateKeys = Object.keys(localStorage).filter(key => key.startsWith('reduxState_'));
        
        // Clear other localStorage except language, firstLogin, and all Redux state keys
        clearLocalStorageExcept({ values: ["language", "firstLogin", ...reduxStateKeys] });

        navigate("/login");
        return null;
      }
      
      throw err;
    }
  }, [callUserDetails, dispatch, navigate]);

  return { fetchUserDetails, userInfo, userDefaultAddressId };
};

export default useUser;
